import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { Layout, Spin, Result, message } from 'antd'
import useAdmin from '@/hooks/swr/useAdmin'
import { useMatch } from '@/hooks/swr/useMatch'
import { useBanquetRounds } from '@/hooks/swr/useBanquetRounds'
import TestMatchBanner from '@/components/TestMatchBanner'
import { map } from 'lodash-es'
import { useMediaQuery } from 'react-responsive'
import { track } from '@/utils/track'
import {
  BookOutlined,
  ClockCircleOutlined,
  DesktopOutlined,
  GiftOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  OrderedListOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from '@ant-design/icons'
import { useModel } from '@/plugin-model/useModel'
import { xs, lg } from '@/utils/responsive'
import { NextSeo } from 'next-seo'
import { isPickleballMatch, isSinglePlayerMatch } from '@/utils/utils'
import useLogin from '@/hooks/swr/useLogin'
import IconFont from './IconFonts'
import Button from './Button'
import { DarkThemeContext } from './DarkTheme'

const { Content } = Layout

const MatchLayout = ({ children }) => {
  const { bgColor } = useContext(DarkThemeContext)
  const isLg = useMediaQuery(lg)
  const isXs = useMediaQuery(xs)
  const router = useRouter()
  const {
    query: { match_id },
  } = router
  const matchURL = `/match/${match_id}`
  const { isAdmin } = useAdmin(match_id as string)
  const { current_user } = useLogin()

  const [autoPlay, setAutoPlay] = useState(false)
  const { view, viewSet } = useModel('view')
  const [interverId, setIntervalId] = useState(undefined)
  const { data: match } = useMatch(match_id)
  const [settingsExpanded, setSettingsExpanded] = useState(false)
  const { data: banquetRounds } = useBanquetRounds(match.match_type === 'banquet' ? (match_id as string) : null)

  const isSettingsPage = router.asPath.includes(`/match/${match_id}/settings`)

  useEffect(() => {
    if (isSettingsPage) {
      setSettingsExpanded(true)
    }
  }, [isSettingsPage])

  useEffect(() => {
    if (match.is_private && isAdmin === false) {
      message.info('私人赛禁止查看')
      router.push('/404')
    }
    if (match.hidden && !current_user.is_gd_admin) {
      message.info('比赛已删除')
      router.push('/404')
    }
  }, [current_user, current_user.is_gd_admin, isAdmin, match.hidden, match.is_private, router])

  const menuItems = useMemo(() => {
    // Banquet type menu
    if (match.match_type === 'banquet') {
      return [
        isAdmin
          ? {
              key: 'banquet_settings',
              label: (
                <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/banquet/settings`)}>
                  <SettingOutlined />
                  活动设置
                </Button>
              ),
            }
          : null,
        isAdmin
          ? {
              key: 'banquet_participants',
              label: (
                <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/banquet/participants`)}>
                  <UserOutlined />
                  参与者管理
                </Button>
              ),
            }
          : null,
        // Dynamic rounds menu
        ...(banquetRounds || []).map((round) => ({
          key: `banquet_${round.round_type}`,
          label: (
            <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/banquet/${round.round_type}`)}>
              <GiftOutlined />
              {round.name}
            </Button>
          ),
        })),
        isAdmin
          ? {
              key: 'presentation',
              label: (
                <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/presentation`)}>
                  <DesktopOutlined />
                  投屏控制
                </Button>
              ),
            }
          : null,
      ].filter((item) => item !== null)
    }

    // Original menu for other match types
    return [
      isSinglePlayerMatch(match.match_type)
        ? {
            key: 'players_score',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/players_score`)}>
                <OrderedListOutlined />
                成绩排名
              </Button>
            ),
          }
        : {
            key: 'teams_score',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/teams_score`)}>
                <OrderedListOutlined />
                成绩排名
              </Button>
            ),
          },
      {
        key: 'rooms',
        label: (
          <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}`)}>
            <DesktopOutlined />
            {match.match_type === 'group_round_robin' ? '对阵表' : isPickleballMatch(match.match_type) ? '场地编排' : '桌次编排'}
          </Button>
        ),
      },
      match.need_guild_rank
        ? {
            key: 'guild_rank',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/guild_rank`)}>
                <OrderedListOutlined />
                团体排名
              </Button>
            ),
          }
        : null,
      isAdmin
        ? isSinglePlayerMatch(match.match_type)
          ? {
              key: 'players',
              label: (
                <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/players`)}>
                  <UserOutlined />
                  选手管理
                </Button>
              ),
            }
          : {
              key: 'teams',
              label: (
                <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/teams`)}>
                  <TeamOutlined />
                  队伍管理
                </Button>
              ),
            }
        : null,
      isAdmin
        ? {
            key: 'penalty',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/penalty`)}>
                <ExclamationCircleOutlined />
                比赛判罚
              </Button>
            ),
          }
        : null,
      isAdmin
        ? {
            key: 'timer',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/timer`)}>
                <ClockCircleOutlined />
                比赛计时
              </Button>
            ),
          }
        : null,
      isAdmin
        ? {
            key: 'presentation',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/presentation`)}>
                <DesktopOutlined />
                投屏控制
              </Button>
            ),
          }
        : null,
      isAdmin
        ? {
            key: 'settings',
            label: (
              <Button type="text" className="text-primary" onClick={() => setSettingsExpanded(!settingsExpanded)}>
                <SettingOutlined />
                比赛设置
                {settingsExpanded ? <CaretDownOutlined className="text-primary" /> : <CaretRightOutlined className="text-primary" />}
              </Button>
            ),
            expanded: settingsExpanded,
            onClick: () => setSettingsExpanded(!settingsExpanded),
            children: [
              {
                key: 'settings_set',
                label: (
                  <Button type="link" onClick={() => router.push(`/match/${match_id}/settings`)}>
                    赛事设置
                  </Button>
                ),
              },
              {
                key: 'settings_fairness',
                label: (
                  <Button type="link" className="!font-bold !text-[#f83232]" onClick={() => router.push(`/match/${match_id}/settings/fairness`)}>
                    🛡️ 赛事公平诚信
                  </Button>
                ),
              },
              {
                key: 'settings_info',
                label: (
                  <Button type="link" onClick={() => router.push(`/match/${match_id}/settings/info`)}>
                    基础信息
                  </Button>
                ),
              },
              {
                key: 'settings_prize',
                label: (
                  <Button type="link" onClick={() => router.push(`/match/${match_id}/settings/prize`)}>
                    奖项 / 团体
                  </Button>
                ),
              },
              {
                key: 'settings_vip',
                label: (
                  <Button type="link" onClick={() => router.push(`/match/${match_id}/settings/vip`)}>
                    VIP专区
                  </Button>
                ),
              },
              {
                key: 'settings_online',
                label: (
                  <Button disabled={!match.allow_online_game} type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/settings/online`)}>
                    线上赛设置
                  </Button>
                ),
                disabled: !match.allow_online_game,
              },
              {
                key: 'document',
                label: (
                  <Button type="link" onClick={() => router.push(`/match/${match_id}/document`)}>
                    赛事文档
                  </Button>
                ),
              },
            ].filter((item) => item !== null),
          }
        : null,
      isAdmin && match.is_count_elo_rank
        ? {
            key: 'certificates',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/certificates`)}>
                <BookOutlined />
                电子证书
              </Button>
            ),
          }
        : null,
      isAdmin && match.match_type?.includes('duplicated')
        ? {
            key: 'duplicated_settings',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/duplicated_settings`)}>
                <SettingOutlined />
                复式设置
              </Button>
            ),
          }
        : null,
      isAdmin && match.match_type === 'league'
        ? {
            key: 'league_settings',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/league_settings`)}>
                <SettingOutlined />
                联赛设置
              </Button>
            ),
          }
        : null,
      match.match_type === 'league'
        ? {
            key: 'league_teams_score',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/league_teams_score`)}>
                <OrderedListOutlined />
                联赛得分
              </Button>
            ),
          }
        : null,
      isAdmin
        ? {
            key: 'lottery',
            label: (
              <Button type="link" className="text-primary" onClick={() => router.push(`/match/${match_id}/lottery`)}>
                <GiftOutlined />
                灵缇抽奖
              </Button>
            ),
          }
        : null,
    ]
  }, [isAdmin, match.allow_online_game, match.is_count_elo_rank, match.match_type, match.need_guild_rank, match_id, router, settingsExpanded, banquetRounds])

  useEffect(() => {
    if (isLg) {
      viewSet('fullScreen', false)
    } else {
      viewSet('fullScreen', true)
    }
  }, [isLg, viewSet])

  if (match.ok === undefined && match.id === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spin indicator={<LoadingOutlined />} />
      </div>
    )
  }

  if (match.id === undefined && match.ok === false) {
    return (
      <div className="flex h-full items-center justify-center">
        <Result
          status="403"
          title="403"
          subTitle={match.message || '无权限访问或比赛不存在'}
          extra={
            <Button type="primary" onClick={() => router.push('/match')}>
              返回比赛列表
            </Button>
          }
        />
      </div>
    )
  }

  const isSubPathActive = (key) => {
    const currentPath = router.asPath
    switch (key) {
      case 'settings_set':
        return currentPath === `/match/${match_id}/settings`
      case 'settings_fairness':
        return currentPath.includes('/settings/fairness')
      case 'settings_info':
        return currentPath.includes('/settings/info')
      case 'settings_prize':
        return currentPath.includes('/settings/prize')
      case 'settings_vip':
        return currentPath.includes('/settings/vip')
      case 'settings_online':
        return currentPath.includes('/settings/online')
      default:
        return false
    }
  }

  return (
    <Layout className="h-full" hasSider>
      <NextSeo title={match.name} description="灵缇互娱是国内领先的赛事平台，以专业的赛事支持，细致的赛事服务，逐渐成为行业的领头羊。" />
      <div
        className={`fixed top-[calc(4rem_+_var(--banner-h,0px))] z-[1000] h-[calc(100vh_-_60px_-_var(--banner-h,0px))] transform overflow-auto bg-[#001529] px-4 text-center duration-300 ease-in-out ${
          view.fullScreen ? 'w-full -translate-x-full md:w-[160px]' : 'w-full translate-x-0 md:w-[160px]'
        }`}
      >
        {map(
          menuItems.filter((v) => v !== null),
          (item) => (
            <div key={item.key}>
              <div
                className={`my-3 w-full rounded-lg py-1 text-xl md:text-sm ${
                  (router.asPath.split('/').length < 4 && item.key === 'rooms') || (item.key !== 'settings' && router.asPath.split(`${matchURL}/`).slice(1, 2).includes(item.key))
                    ? 'bg-white/20'
                    : ''
                }`}
                onClick={() => {
                  track('match_feature', { target: item.key })
                  if (item.onClick) {
                    item.onClick()
                  }
                  if (isXs) {
                    viewSet('fullScreen', true)
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="mr-2 text-primary">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                  {item.expandIcon && <span className="text-xs">{item.expandIcon}</span>}
                </div>
              </div>
              {item.children && item.expanded && (
                <div className="ml-4">
                  {map(item.children, (child) => (
                    <div
                      key={child.key}
                      className={`my-2 w-full rounded-lg text-lg md:text-xs 
                        ${isSubPathActive(child.key) ? 'bg-white/10' : ''}
                        ${child.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                      `}
                      onClick={() => {
                        if (!child.disabled && isXs) {
                          viewSet('fullScreen', true)
                        }
                      }}
                    >
                      {child.disabled ? <span className="text-gray-500">{child.label}</span> : child.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 z-max mb-14 flex h-0 w-[160px] justify-around text-white">
        <div
          className="hidden w-fit cursor-pointer sm:block"
          onClick={() => {
            if (!autoPlay) {
              const matchContent = document.getElementById('match-content')
              if (!matchContent) {
                return
              }
              matchContent.scrollTo({
                top: matchContent.scrollTop + 175,
                behavior: 'smooth',
              })
              const thisID = setInterval(() => {
                matchContent.scrollTo({
                  top: matchContent.scrollTop + 175,
                  behavior: 'smooth',
                })
                if (Math.ceil(matchContent.clientHeight + matchContent.scrollTop) >= matchContent.scrollHeight) {
                  matchContent.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  })
                }
              }, 3000)

              setIntervalId(thisID)
              setAutoPlay(true)
            } else {
              clearInterval(interverId)

              setIntervalId(undefined)
              setAutoPlay(false)
            }
          }}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/[0.9]">
            <IconFont icon="icon-scroll" className={`text-lg hover:text-blue-300 ${autoPlay && 'text-blue-500'}`} />
          </div>
        </div>
        <div className="hidden w-fit cursor-pointer sm:block">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full bg-black/[0.9]"
            onClick={() => {
              viewSet('fullScreen', !view.fullScreen)
            }}
          >
            <IconFont icon={`${view.fullScreen ? 'icon-uncollapsed' : 'icon-collapsed'}`} className="text-lg hover:text-blue-300" />
          </div>
        </div>
      </div>
      <Content className={`z-50 h-full w-full transition-all duration-300 ${view.fullScreen ? '' : 'md:ml-[160px]'}`}>
        <div id="match-content" className={`z-50 h-full w-full overflow-auto ${bgColor}`}>
          <TestMatchBanner match={match} />
          {children}
        </div>
      </Content>
    </Layout>
  )
}

export default MatchLayout
